import React from 'react';
import { Activity, Gauge, HardDrive, RefreshCw, Wifi } from 'lucide-react';
import { PanelSection } from '../ui/PanelSection';
import { Chip } from '../ui/Chip';
import { EmptyNote } from '../ui/EmptyNote';
import { useRcuLink } from '../useRcuLink';
import { BENCH_CHECKS, PROTOCOL_CAVEATS } from '../../../lib/rcu/catalog/caveats';
import { TONE } from '../../../lib/rcu/catalog/feedback';
import { REPORTED_STATUS } from '../../../lib/rcu/catalog/ack';
import { MEMORY_PRESSURE } from '../../../lib/rcu/catalog/link';
import {
  DIN_VALUE,
  RCU_GROUP,
  dinValueLabel,
  dintypeHex,
  moduleIdOfRail,
  relayValueLabel,
} from '../../../lib/rcu/catalog/protocol';
import { RAIL_TYPE_SPECS } from '../../../lib/rcu/catalog/railType';
import { consoleChannels, firmwareChannel } from '../../../lib/rcu/catalog/dinValue';
import { readAllDin, readAllRelays, readStatus } from '../../../lib/rcu/link/commands';
import { dinKey, dinModuleKey, relayKey } from '../../../lib/rcu/link/live';
import type { LiveState } from '../../../lib/rcu/link/live';
import { formatBytes } from '../../../lib/rcu/link/coredump';
import { useRcuConfigStore } from '../../../stores/rcuConfigStore';
import { useRcuLinkStore } from '../../../stores/rcuLinkStore';

/** A labelled value, the panel's smallest unit. */
const Metric: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <div className="rounded-xl bg-[#F5F5F5] px-3 py-2">
    <p className="text-xs text-[#666666]">{label}</p>
    <p className="mt-0.5 truncate font-mono text-sm text-[#333333]">{value}</p>
    {hint !== undefined && <p className="mt-0.5 text-xs text-[#666666]">{hint}</p>}
  </div>
);

const MemoryBar: React.FC<{ label: string; used: number; total: number }> = ({ label, used, total }) => {
  const share = total === 0 ? 0 : Math.round((used / total) * MEMORY_PRESSURE.FULL_PERCENT);
  return (
    <div>
      <div className="flex items-baseline justify-between text-xs text-[#666666]">
        <span>{label}</span>
        <span className="font-mono">
          {formatBytes(total - used)} free of {formatBytes(total)}
        </span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-[#E0E0E0]">
        <div
          className={`h-full rounded-full ${
            share >= MEMORY_PRESSURE.CRITICAL_PERCENT
              ? 'bg-[#F44336]'
              : share >= MEMORY_PRESSURE.WARN_PERCENT
                ? 'bg-[#FF9800]'
                : 'bg-[#4CAF50]'
          }`}
          style={{ width: `${share}%` }}
        />
      </div>
    </div>
  );
};

const StatusCard: React.FC<{ live: LiveState }> = ({ live }) => {
  const status = live.status;
  if (status === null) {
    return (
      <EmptyNote message="No status read yet. The RCU also publishes its status on the events topic, so it may appear on its own." />
    );
  }

  const online = status.status === REPORTED_STATUS.ONLINE;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Chip label={status.status} tone={online ? TONE.SUCCESS : TONE.NEUTRAL} />
        <span className="font-mono text-sm text-[#333333]">{status.serial}</span>
        {status.ip !== '' && <span className="font-mono text-sm text-[#666666]">{status.ip}</span>}
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Uptime" value={status.uptime === '' ? '—' : status.uptime} />
        <Metric
          label="Wi-Fi"
          value={status.wifi === null ? '—' : `${status.wifi.ssid}`}
          hint={status.wifi === null ? undefined : `${status.wifi.rssi} dBm · channel ${status.wifi.channel}`}
        />
        <Metric
          label="Firmware"
          value={status.firmware === null ? '—' : status.firmware.version}
          hint={status.firmware?.buildDate}
        />
        <Metric label="Last reading" value={status.mac} hint="Hardware address" />
      </div>

      {status.memory !== null && (
        <div className="space-y-3">
          <MemoryBar
            label="Internal memory"
            used={status.memory.internal.total - status.memory.internal.free}
            total={status.memory.internal.total}
          />
          <MemoryBar
            label="PSRAM"
            used={status.memory.psram.total - status.memory.psram.free}
            total={status.memory.psram.total}
          />
        </div>
      )}
    </div>
  );
};

/**
 * What the device is doing: its own status document, the relay bank as last read,
 * and every configured DIN module, with the protocol's uncertainties stated
 * rather than hidden.
 */
export const StatusPanel: React.FC = () => {
  const config = useRcuConfigStore((state) => state.config);
  const live = useRcuLinkStore((state) => state.live);
  const lastReadingAt = useRcuLinkStore((state) => state.lastReadingAt);
  const { send, inFlight } = useRcuLink();

  const relaysOn = live.allRelays?.filter((value) => value !== DIN_VALUE.MIN).length ?? null;

  return (
    <div className="space-y-5">
      <PanelSection
        title="Device status"
        description="Read it now, or wait for the RCU's own status message."
        action={
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              disabled={inFlight > 0}
              onClick={() => void send(readStatus(), 'Status read')}
              className="flex items-center gap-2 rounded-full bg-[#1A1A1A] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#333333] disabled:opacity-50"
            >
              <RefreshCw className="h-4 w-4" />
              Read status
            </button>
          </div>
        }
      >
        <StatusCard live={live} />
        {lastReadingAt !== null && (
          <p className="mt-3 text-xs text-[#666666]">
            Last message at {new Date(lastReadingAt).toLocaleTimeString()}.
          </p>
        )}
      </PanelSection>

      <PanelSection
        title="Relay bank"
        description={`26 outputs on the RCU itself. Ordinary use means group ${RCU_GROUP.DEFAULT}; groups ${RCU_GROUP.MIN}–${RCU_GROUP.MAX} are subsets the firmware exposes.`}
        action={
          <button
            type="button"
            disabled={inFlight > 0}
            onClick={() => void send(readAllRelays(), 'Relay read')}
            className="flex shrink-0 items-center gap-2 rounded-full border border-[#E0E0E0] px-4 py-2 text-sm font-medium text-[#666666] transition-colors hover:border-[#D4A054] hover:text-[#333333] disabled:opacity-50"
          >
            <Gauge className="h-4 w-4" />
            Read all
          </button>
        }
      >
        {live.allRelays === null ? (
          <EmptyNote message="No full relay read yet. Individual replies fill in the tiles as they arrive." />
        ) : (
          <div className="mb-4 flex items-center gap-3">
            <Chip label={`${relaysOn ?? 0} on`} tone={(relaysOn ?? 0) > 0 ? TONE.SUCCESS : TONE.NEUTRAL} />
            <span className="text-sm text-[#666666]">
              A dashed tile has not been read; the console files every report under group{' '}
              {RCU_GROUP.DEFAULT}.
            </span>
          </div>
        )}
        <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-10 lg:grid-cols-[repeat(13,minmax(0,1fr))]">
          {Array.from({ length: 26 }, (_unused, index) => index + 1).map((relay) => {
            const value = live.relays[relayKey(RCU_GROUP.DEFAULT, relay)];
            const isOn = value !== undefined && value !== DIN_VALUE.MIN;
            return (
              <div
                key={relay}
                title={value === undefined ? `Relay ${relay}: not read` : `Relay ${relay}: ${relayValueLabel(value)}`}
                className={`flex aspect-square flex-col items-center justify-center rounded-lg border text-xs font-medium ${
                  value === undefined
                    ? 'border-dashed border-[#E0E0E0] text-[#999999]'
                    : isOn
                      ? 'border-[#D4A054] bg-[#FDF6E9] text-[#333333]'
                      : 'border-[#E0E0E0] bg-white text-[#666666]'
                }`}
              >
                <span className="font-mono">{relay}</span>
              </div>
            );
          })}
        </div>
      </PanelSection>

      <PanelSection
        title="DIN modules"
        description="The DIN rails this configuration defines, read channel by channel."
        action={
          <button
            type="button"
            disabled={inFlight > 0 || config.rails.length === 0}
            onClick={() =>
              void Promise.all(
                config.rails.map((rail) =>
                  send(
                    readAllDin({ group: RCU_GROUP.DEFAULT, module: moduleIdOfRail(rail.type), addr: rail.address }),
                    `${RAIL_TYPE_SPECS[rail.type].shortLabel} ${rail.address} read`,
                  ),
                ),
              )
            }
            className="flex shrink-0 items-center gap-2 rounded-full border border-[#E0E0E0] px-4 py-2 text-sm font-medium text-[#666666] transition-colors hover:border-[#D4A054] hover:text-[#333333] disabled:opacity-50"
          >
            <HardDrive className="h-4 w-4" />
            Read every module
          </button>
        }
      >
        {config.rails.length === 0 ? (
          <EmptyNote message="No DIN rails are defined yet. Add them under DIN rails." />
        ) : (
          <ul className="space-y-3">
            {config.rails.map((rail) => {
              const spec = RAIL_TYPE_SPECS[rail.type];
              const moduleId = moduleIdOfRail(rail.type);
              const values = live.dinAll[dinModuleKey(moduleId, rail.address)];
              return (
                <li key={rail.id} className="rounded-xl border border-[#E0E0E0] px-3 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#333333]">
                        {spec.label} · address {rail.address}
                      </p>
                      <p className="font-mono text-xs text-[#666666]">
                        dintype {dintypeHex(moduleId)} · {spec.channels} channels
                      </p>
                    </div>
                    <button
                      type="button"
                      disabled={inFlight > 0}
                      onClick={() =>
                        void send(
                          readAllDin({ group: RCU_GROUP.DEFAULT, module: moduleIdOfRail(rail.type), addr: rail.address }),
                          `${spec.shortLabel} ${rail.address} read`,
                        )
                      }
                      className="shrink-0 rounded-full border border-[#E0E0E0] px-3 py-1.5 text-xs font-medium text-[#666666] transition-colors hover:border-[#D4A054] disabled:opacity-50"
                    >
                      Read module
                    </button>
                  </div>
                  <div className="mt-2 grid grid-cols-8 gap-1">
                    {/* Labelled 1-4 as the module is; indexed from 0 on the wire. */}
                    {consoleChannels(spec.channels).map((channel) => {
                      const wire = firmwareChannel(channel);
                      const value = live.din[dinKey(moduleId, rail.address, wire)];
                      const read = values?.[wire] ?? value;
                      return (
                        <div
                          key={channel}
                          title={`Channel ${channel}: ${read === undefined ? 'not read' : dinValueLabel(read)}`}
                          className={`rounded-lg border px-1 py-1 text-center text-[11px] ${
                            read === undefined
                              ? 'border-dashed border-[#E0E0E0] text-[#999999]'
                              : read === DIN_VALUE.MIN
                                ? 'border-[#E0E0E0] bg-white text-[#666666]'
                                : 'border-[#D4A054] bg-[#FDF6E9] text-[#333333]'
                          }`}
                        >
                          <span className="block font-mono">{channel}</span>
                          <span className="block">{read === undefined ? '—' : read}</span>
                        </div>
                      );
                    })}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </PanelSection>

      <PanelSection
        title="What this console assumes"
        description="Read from the RCU's command reference. The uncertain parts are worth a bench check."
      >
        <ul className="space-y-1.5 text-sm text-[#666666]">
          {PROTOCOL_CAVEATS.map((caveat) => (
            <li key={caveat} className="flex gap-2">
              <Activity className="mt-0.5 h-4 w-4 shrink-0 text-[#D4A054]" />
              <span>{caveat}</span>
            </li>
          ))}
        </ul>
        <div className="mt-4 rounded-xl bg-[#F5F5F5] px-3 py-3">
          <p className="flex items-center gap-2 text-sm font-medium text-[#333333]">
            <Wifi className="h-4 w-4 text-[#666666]" />
            Bench checks
          </p>
          <ol className="mt-1.5 list-decimal space-y-1 pl-5 text-sm text-[#666666]">
            {BENCH_CHECKS.map((check) => (
              <li key={check}>{check}</li>
            ))}
          </ol>
        </div>
      </PanelSection>
    </div>
  );
};

export default StatusPanel;
